import { useMemo, useState, type CSSProperties } from 'react';
import { Cable } from 'lucide-react';
import { endpointKey, type HarnessWire } from '../harnessDerive';

/**
 * A clean, read-only "what this harness actually is" view, derived live from
 * the schematic wires assigned to it. Connectors are grouped by component into
 * a single box per component (its connectors shown as sections), the most-
 * connected component on the left as a hub, the rest fanned on the right, and
 * every wire drawn as a coloured line labelled with its net. Drawn on white
 * "paper" so real wire colours read true; the whole thing scales to fit the
 * panel width so wide harnesses don't overflow.
 */

const BOX_W = 212;
const HEADER_H = 38;
const CONN_H = 22;
const ROW_H = 22;
const BOX_GAP = 26;
const CONN_GAP = 6;
const PAD = 26;
// Nets are labelled at the pins (not mid-wire), so the run between the boxes
// only needs enough room to read the crossings — keep it compact so the whole
// diagram stays narrow and fits the panel.
const WIRE_SPAN = 210;

interface PinRow {
  pin: string;
  net: string;
  y: number;
}
interface ConnSection {
  connector: string;
  labelY: number;
  pins: PinRow[];
}
interface CompBox {
  componentId: string;
  component: string;
  x: number;
  y: number;
  h: number;
  col: 'L' | 'R';
  connectors: ConnSection[];
}
interface WireLine {
  d: string;
  color: string;
  aKey: string;
  bKey: string;
}

const naturalCmp = (a: string, b: string) =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
const trunc = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

function buildLayout(wires: HarnessWire[]) {
  if (wires.length === 0) return null;

  // 1. endpoints per (component, connector)
  const epMap = new Map<
    string,
    { componentId: string; component: string; connector: string; pins: Set<string>; touches: number }
  >();
  const ep = (id: string, comp: string, conn: string) => {
    const k = endpointKey(id, conn);
    let e = epMap.get(k);
    if (!e) {
      e = { componentId: id, component: comp, connector: conn, pins: new Set(), touches: 0 };
      epMap.set(k, e);
    }
    return e;
  };
  for (const w of wires) {
    const f = ep(w.fromCompId, w.fromComp, w.fromConnector);
    f.touches += 1;
    f.pins.add(w.fromPin || '');
    const t = ep(w.toCompId, w.toComp, w.toConnector);
    t.touches += 1;
    t.pins.add(w.toPin || '');
  }

  // 2. group connectors by component (so one component = one box, its
  //    connectors stacked inside it)
  const comps = new Map<
    string,
    { componentId: string; component: string; connectors: { connector: string; pins: string[] }[]; touches: number }
  >();
  const order: string[] = [];
  for (const e of epMap.values()) {
    let c = comps.get(e.componentId);
    if (!c) {
      c = { componentId: e.componentId, component: e.component, connectors: [], touches: 0 };
      comps.set(e.componentId, c);
      order.push(e.componentId);
    }
    c.connectors.push({ connector: e.connector, pins: [...e.pins].sort(naturalCmp) });
    c.touches += e.touches;
  }
  const compList = order.map((id) => comps.get(id)!);
  for (const c of compList) c.connectors.sort((a, b) => naturalCmp(a.connector, b.connector));

  // Net carried by each pin — shown beside the pin so you can read a connection
  // at its endpoints (match the same net name on both sides) without tracing
  // the crossing wire.
  const pinNet = new Map<string, string>();
  for (const w of wires) {
    const net = w.netName || w.label || w.wireNo || '';
    if (!net) continue;
    const fk = `${w.fromCompId}||${w.fromConnector}||${w.fromPin || ''}`;
    const tk = `${w.toCompId}||${w.toConnector}||${w.toPin || ''}`;
    if (!pinNet.has(fk)) pinNet.set(fk, net);
    if (!pinNet.has(tk)) pinNet.set(tk, net);
  }

  // 3. hub (most-connected component) on the left, everything else on the right
  const hubId = [...compList].sort((a, b) => b.touches - a.touches)[0].componentId;
  const leftComps = compList.filter((c) => c.componentId === hubId);
  const rightComps = compList.filter((c) => c.componentId !== hubId);

  const leftX = PAD;
  const rightX = PAD + BOX_W + WIRE_SPAN;
  const compBoxes: CompBox[] = [];
  const pinPos = new Map<string, { x: number; y: number; col: 'L' | 'R' }>();

  const place = (list: typeof compList, x: number, col: 'L' | 'R') => {
    let y = PAD;
    for (const c of list) {
      const connectors: ConnSection[] = [];
      let cursor = y + HEADER_H;
      for (const conn of c.connectors) {
        const labelY = cursor;
        cursor += CONN_H;
        const pinList = conn.pins.length ? conn.pins : [''];
        const pins: PinRow[] = [];
        for (const pin of pinList) {
          const py = cursor + ROW_H / 2;
          const dotX = col === 'L' ? x + BOX_W : x;
          const key = `${c.componentId}||${conn.connector}||${pin}`;
          pinPos.set(key, { x: dotX, y: py, col });
          pins.push({ pin, net: pinNet.get(key) ?? '', y: py });
          cursor += ROW_H;
        }
        connectors.push({ connector: conn.connector, labelY, pins });
        cursor += CONN_GAP;
      }
      const h = cursor - y - CONN_GAP + 10;
      compBoxes.push({ componentId: c.componentId, component: c.component, x, y, h, col, connectors });
      y += h + BOX_GAP;
    }
    return y - BOX_GAP;
  };
  const leftBottom = place(leftComps, leftX, 'L');
  const rightBottom = place(rightComps, rightX, 'R');

  const height = Math.max(leftBottom, rightBottom, PAD + HEADER_H + ROW_H) + PAD;
  const width = (rightComps.length ? rightX + BOX_W : leftX + BOX_W) + PAD;

  const wireLines: WireLine[] = [];
  wires.forEach((w) => {
    const aKey = `${w.fromCompId}||${w.fromConnector}||${w.fromPin || ''}`;
    const bKey = `${w.toCompId}||${w.toConnector}||${w.toPin || ''}`;
    const a = pinPos.get(aKey);
    const b = pinPos.get(bKey);
    if (!a || !b || a === b) return;
    const dx = Math.max(46, Math.abs(b.x - a.x) * 0.4);
    const c1x = a.col === 'L' ? a.x + dx : a.x - dx;
    const c2x = b.col === 'L' ? b.x + dx : b.x - dx;
    wireLines.push({
      d: `M ${a.x} ${a.y} C ${c1x} ${a.y}, ${c2x} ${b.y}, ${b.x} ${b.y}`,
      color: w.color || '#94a3b8',
      aKey,
      bKey,
    });
  });

  return { width, height, compBoxes, wireLines };
}

export function HarnessDiagram({ wires }: { wires: HarnessWire[] }) {
  const layout = useMemo(() => buildLayout(wires), [wires]);
  // Click a pin to trace its connection: the pin, the wire(s) it carries and
  // the pin(s) at the far end light up while everything else dims.
  const [selected, setSelected] = useState<string | null>(null);

  if (!layout) {
    return (
      <div style={emptyStyle}>
        <Cable size={30} strokeWidth={1.4} />
        <div style={{ fontWeight: 700, marginTop: 8 }}>No wires in this harness</div>
        <div style={{ fontSize: 12, marginTop: 4, textAlign: 'center', maxWidth: 340 }}>
          Assign wires to this harness in a schematic sheet — they'll appear here as a clean
          connector-to-connector view.
        </div>
      </div>
    );
  }

  const { width, height, compBoxes, wireLines } = layout;

  // Pins to highlight when one is selected: the selection plus the far end of
  // every wire it carries.
  const highlighted = selected ? new Set<string>([selected]) : null;
  if (highlighted) {
    for (const w of wireLines) {
      if (w.aKey === selected) highlighted.add(w.bKey);
      else if (w.bKey === selected) highlighted.add(w.aKey);
    }
  }

  return (
    <div style={scrollStyle}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ display: 'block', width: '100%', height: 'auto', maxHeight: '58vh' }}
      >
        {/* click empty space to clear the current pin selection */}
        <rect x={0} y={0} width={width} height={height} fill="transparent" onClick={() => setSelected(null)} />

        {/* wires first, thin + calm, so the connector boxes and pin/net labels
            read as the primary content and the lines just show routing */}
        {wireLines.map((w, i) => {
          const active = !selected || w.aKey === selected || w.bKey === selected;
          return (
            <path
              key={i}
              d={w.d}
              fill="none"
              stroke={w.color}
              strokeWidth={selected && active ? 3.2 : 1.8}
              strokeLinecap="round"
              opacity={active ? (selected ? 1 : 0.8) : 0.1}
            />
          );
        })}

        {compBoxes.map((b) => (
          <g key={b.componentId}>
            <rect x={b.x} y={b.y} width={BOX_W} height={b.h} rx={8} fill="#ffffff" stroke="#334155" strokeWidth={1.5} />
            {/* component header */}
            <path
              d={`M ${b.x + 8} ${b.y} h ${BOX_W - 16} a 8 8 0 0 1 8 8 v ${HEADER_H - 8} h ${-BOX_W} v ${-(HEADER_H - 8)} a 8 8 0 0 1 8 -8 z`}
              fill="#e2e8f0"
            />
            <text x={b.x + 12} y={b.y + 24} fontSize={13} fontWeight={800} fill="#0f172a">
              {trunc(b.component, 26)}
            </text>

            {b.connectors.map((conn) => (
              <g key={conn.connector || 'default'}>
                {/* connector section label (outer edge, so it never collides
                    with the pin names on the inner edge) */}
                <rect x={b.x} y={conn.labelY} width={BOX_W} height={CONN_H} fill="#f1f5f9" />
                <line x1={b.x} y1={conn.labelY} x2={b.x + BOX_W} y2={conn.labelY} stroke="#e2e8f0" />
                <text
                  x={b.col === 'L' ? b.x + 12 : b.x + BOX_W - 12}
                  y={conn.labelY + 15}
                  textAnchor={b.col === 'L' ? 'start' : 'end'}
                  fontSize={10}
                  fontWeight={800}
                  letterSpacing={0.4}
                  fill="#64748b"
                >
                  {trunc((conn.connector || 'Pins').toUpperCase(), 26)}
                </text>

                {conn.pins.map((p, pi) => {
                  const dotX = b.col === 'L' ? b.x + BOX_W : b.x;
                  const key = `${b.componentId}||${conn.connector}||${p.pin}`;
                  const on = highlighted?.has(key) ?? false;
                  const dim = highlighted != null && !on;
                  return (
                    <g
                      key={pi}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelected((s) => (s === key ? null : key))}
                    >
                      {/* full-row hit area + selection highlight */}
                      <rect
                        x={b.x}
                        y={p.y - ROW_H / 2}
                        width={BOX_W}
                        height={ROW_H}
                        fill={on ? 'rgba(37,99,235,0.13)' : 'transparent'}
                        opacity={dim ? 0.5 : 1}
                      />
                      {/* pin name on the INNER edge next to its dot; the net it
                          carries on the OUTER edge — a full-width row, so labels
                          never overlap and each connection is readable at its
                          endpoints. */}
                      <text
                        x={b.col === 'L' ? b.x + BOX_W - 14 : b.x + 14}
                        y={p.y + 4}
                        textAnchor={b.col === 'L' ? 'end' : 'start'}
                        fontSize={11}
                        fontWeight={700}
                        fill={on ? '#1d4ed8' : '#0f172a'}
                        opacity={dim ? 0.4 : 1}
                      >
                        {p.pin || '—'}
                      </text>
                      {p.net && (
                        <text
                          x={b.col === 'L' ? b.x + 12 : b.x + BOX_W - 12}
                          y={p.y + 4}
                          textAnchor={b.col === 'L' ? 'start' : 'end'}
                          fontSize={10}
                          fontWeight={on ? 700 : 400}
                          fill={on ? '#2563eb' : '#64748b'}
                          opacity={dim ? 0.4 : 1}
                        >
                          {trunc(p.net, 16)}
                        </text>
                      )}
                      <circle
                        cx={dotX}
                        cy={p.y}
                        r={on ? 5 : 3.5}
                        fill={on ? '#2563eb' : '#334155'}
                        opacity={dim ? 0.4 : 1}
                      />
                    </g>
                  );
                })}
              </g>
            ))}
          </g>
        ))}
      </svg>
    </div>
  );
}

const scrollStyle: CSSProperties = {
  flex: 1,
  overflow: 'auto',
  padding: 16,
  background: 'var(--bg-canvas)',
};
const emptyStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--text-muted)',
  padding: 40,
};
